import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { getTaskDetails, updateTask } from '../../../../redux/Slices/TasksSlice';
import { getAllDropdownCacheForQuery } from '../../../../shared/utils.js/offlineQueue';
import { getEmployees, getEmployeeDetails, getLabTests, addProject, getProjects, getCustomers, getTaxes, getCostCenters, addTax, addCostCenter, addCustomer } from '../../../../redux/Slices/authSlice';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';
import { saveDropdownCache, getDropdownCache, generateDropdownCacheKey, isOnline } from '../../../../shared/utils.js/offlineQueue';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

// edit task component
const EditTasks = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({
    project_id: '',
    name_ar: '',
    name_en: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    start_date: '',
    end_date: '',
    employee_id: '',
    notes: '',
    lab_test_ids: [],
  });
  const [newAttachments, setNewAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);

  // Project State variables
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPagination, setProjectPagination] = useState(null);
  const projectSearchInputRef = useRef(null);

  // Employee State variables
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);

  // Lab tests state variables
  const [labDropdownOpen, setLabDropdownOpen] = useState(false);
  const [labSearchTerm, setLabSearchTerm] = useState('');
  const [labOptions, setLabOptions] = useState([]);
  const [labPage, setLabPage] = useState(1);
  const [labPagination, setLabPagination] = useState(null);
  const labSearchInputRef = useRef(null);

  // Test modal state
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [testForm, setTestForm] = useState({
    name_ar: '',
    name_en: '',
    is_active: true,
  });
  const [isSubmittingTest, setIsSubmittingTest] = useState(false);

  // Responsive form width state
  const [formMaxWidth, setFormMaxWidth] = useState('700px');

  // Sidebar responsive effect
  useEffect(() => {
    const updateFormWidth = () => {
      const root = document.documentElement;
      const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]');

      if (sidebar) {
        const isCollapsed = sidebar.classList.contains('collapsed') ||
          sidebar.getAttribute('data-collapsed') === 'true' ||
          window.getComputedStyle(sidebar).width === '0px' ||
          sidebar.offsetWidth < 100;

        if (isCollapsed) {
          root.style.setProperty('--form-max-width', '100%');
          setFormMaxWidth('100%');
        } else {
          root.style.setProperty('--form-max-width', '750px');
          setFormMaxWidth('750px');
        }
      }
    };

    updateFormWidth();

    const observer = new MutationObserver(updateFormWidth);
    const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]');
    if (sidebar) {
      observer.observe(sidebar, { attributes: true, attributeFilter: ['class', 'data-collapsed'] });
    }

    window.addEventListener('resize', updateFormWidth);
    window.addEventListener('sidebar-toggle', updateFormWidth);
    document.addEventListener('sidebar-collapsed', updateFormWidth);
    document.addEventListener('sidebar-expanded', updateFormWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFormWidth);
      window.removeEventListener('sidebar-toggle', updateFormWidth);
      document.removeEventListener('sidebar-collapsed', updateFormWidth);
      document.removeEventListener('sidebar-expanded', updateFormWidth);
    };
  }, []);

  // Project modal state
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // Project modal dropdown states
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [customersPagination, setCustomersPagination] = useState(null);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const [taxes, setTaxes] = useState([]);
  const [taxesPagination, setTaxesPagination] = useState(null);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const [costCenters, setCostCenters] = useState([]);
  const [costCentersPagination, setCostCentersPagination] = useState(null);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Tax modal state
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });
  const [isSubmittingTax, setIsSubmittingTax] = useState(false);

  // Cost center modal state
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });
  const [isSubmittingCostCenter, setIsSubmittingCostCenter] = useState(false);

  // Customer modal state
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: ''
  });
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      setIsLoading(true);
      if (!isOnline()) {
        try {
          const allCachedTasks = await getAllDropdownCacheForQuery('tasks', { per_page: 1000 });
          const cachedTask = allCachedTasks.find(t => String(t.id) === String(id));
          if (cachedTask) {
            const details = cachedTask;
            const projectIdValue = details?.project_id ? String(details.project_id) : '';
            const employeeIdValue = details?.employee_id ? String(details.employee_id) : '';
            const labIds =
              Array.isArray(details?.lab_test_ids) && details.lab_test_ids.length > 0
                ? details.lab_test_ids.map((id) => String(id))
                : Array.isArray(details?.lab_tests)
                  ? details.lab_tests.map((test) => String(test.id || test))
                  : [];
            setForm({
              project_id: projectIdValue,
              name_ar: details?.name_ar || '',
              name_en: details?.name_en || '',
              description: details?.description || '',
              status: details?.status || 'pending',
              priority: details?.priority || 'medium',
              start_date: details?.start_date ? details.start_date.split('T')[0] : '',
              end_date: details?.end_date ? details.end_date.split('T')[0] : '',
              employee_id: employeeIdValue,
              notes: details?.notes || '',
              lab_test_ids: labIds,
            });
            const filesFromDetails = details?.files || details?.attachments || [];
            if (Array.isArray(filesFromDetails) && filesFromDetails.length > 0) {
              const normalized = filesFromDetails.map((file, index) => ({
                id: file.id || file.file_id || `existing-${index}`,
                name: file.name || file.file_name || file.original_name || file.title || t('tasks.attachments.fallbackFileName', { index: index + 1, defaultValue: `ملف ${index + 1}` }),
                url: file.url || file.path || file.file_path || file.file,
              }));
              setExistingAttachments(normalized);
            } else {
              setExistingAttachments([]);
            }
            toast.info('لا يوجد اتصال بالإنترنت. يتم عرض البيانات المحفوظة محلياً.', { rtl: true, autoClose: 3000 });
          } else {
            toast.warning('لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة لهذه المهمة.', { rtl: true, autoClose: 3000 });
            navigate('/tasks');
          }
        } catch (error) {
          toast.error('حدث خطأ أثناء تحميل البيانات من الكاش', { rtl: true });
          navigate('/tasks');
        } finally {
          setIsLoading(false);
        }
      } else {
        try {
          const payload = await dispatch(getTaskDetails(id)).unwrap();
          const details = payload?.data || payload;
          const projectIdValue = details?.project_id ? String(details.project_id) : '';
          const employeeIdValue = details?.employee_id ? String(details.employee_id) : '';
          const labIds =
            Array.isArray(details?.lab_test_ids) && details.lab_test_ids.length > 0
              ? details.lab_test_ids.map((id) => String(id))
              : Array.isArray(details?.lab_tests)
                ? details.lab_tests.map((test) => String(test.id || test))
                : [];
          setForm({
            project_id: projectIdValue,
            name_ar: details?.name_ar || '',
            name_en: details?.name_en || '',
            description: details?.description || '',
            status: details?.status || 'pending',
            priority: details?.priority || 'medium',
            start_date: details?.start_date ? details.start_date.split('T')[0] : '',
            end_date: details?.end_date ? details.end_date.split('T')[0] : '',
            employee_id: employeeIdValue,
            notes: details?.notes || '',
            lab_test_ids: labIds,
          });
          const filesFromDetails = details?.files || details?.attachments || [];
          if (Array.isArray(filesFromDetails) && filesFromDetails.length > 0) {
            const normalized = filesFromDetails.map((file, index) => ({
              id: file.id || file.file_id || `existing-${index}`,
              name: file.name || file.file_name || file.original_name || file.title || t('tasks.attachments.fallbackFileName', { index: index + 1, defaultValue: `ملف ${index + 1}` }),
              url: file.url || file.path || file.file_path || file.file,
            }));
            setExistingAttachments(normalized);
          } else {
            setExistingAttachments([]);
          }
          // Save to cache for offline
          try {
            const allCachedTasks = await getAllDropdownCacheForQuery('tasks', { per_page: 1000 });
            const idx = allCachedTasks.findIndex(t => String(t.id) === String(details.id));
            let updatedTasks;
            if (idx !== -1) {
              updatedTasks = [...allCachedTasks];
              updatedTasks[idx] = { ...allCachedTasks[idx], ...details };
            } else {
              updatedTasks = [...allCachedTasks, details];
            }
            const cacheKey = generateDropdownCacheKey('tasks', { page: 1, per_page: 1000 });
            await saveDropdownCache(cacheKey, updatedTasks, {
              current_page: 1,
              last_page: 1,
              per_page: updatedTasks.length,
              total: updatedTasks.length,
            });
          } catch (cacheError) {
            console.error('Error saving task to cache:', cacheError);
          }
          // Fetch selected project and employee to display their names
          const fetchSelectedItems = async () => {
            if (projectIdValue) {
              try {
                const token = getToken();
                const currentLang = i18n.language || 'ar';
                const projectResponse = await axios.get(`${BASE_URL}/projects/${projectIdValue}`, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Accept-Language': currentLang,
                  },
                });
                const projectData = projectResponse.data?.data || projectResponse.data;
                if (projectData) {
                  setProjectOptions((prev) => {
                    const exists = prev.find((p) => String(p.id) === String(projectData.id));
                    return exists ? prev : [...prev, projectData];
                  });
                }
              } catch (error) {
                console.error('Failed to fetch project:', error);
              }
            }
            if (employeeIdValue) {
              try {
                const employeePayload = await dispatch(getEmployeeDetails(employeeIdValue)).unwrap();
                const employeeData = employeePayload?.data || employeePayload;
                if (employeeData) {
                  setEmployeeOptions((prev) => {
                    const exists = prev.find((e) => String(e.id) === String(employeeData.id));
                    return exists ? prev : [...prev, employeeData];
                  });
                }
              } catch (error) {
                console.error(`Failed to fetch employee ${employeeIdValue}:`, error);
              }
            }
          };
          fetchSelectedItems();
        } catch (error) {
          toast.error(error || t('tasks.edit.messages.fetchError'), { rtl: isRTL });
          navigate('/tasks');
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate]);

  // Fetch Projects (with offline cache support)
  const fetchProjects = useCallback(
    async (page = projectPage, search = projectSearchTerm) => {
      try {
        // Generate cache key
        const cacheKey = generateDropdownCacheKey('projects', {
          page,
          per_page: 10,
          search: search || '',
          language: i18n.language,
        });

        // Step 1: Try to load from IndexedDB first (Source of Truth)
        let cachedData = null;
        try {
          cachedData = await getDropdownCache(cacheKey);
          if (cachedData && cachedData.items && Array.isArray(cachedData.items)) {
            setProjectOptions(cachedData.items);
            setProjectPagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.error('EditTasks: Error loading projects from cache:', cacheError);
        }

        // Step 2: Try to fetch from API (for sync/update)
        if (isOnline()) {
          try {
            const token = getToken();
            if (!token) throw new Error(t('tasks.edit.messages.noAuthToken'));
            const currentLang = i18n.language || 'ar';
            const response = await axios.get(`${BASE_URL}/projects`, {
              params: {
                page,
                per_page: 10,
                search: search || undefined,
              },
              headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': currentLang,
              },
            });
            const { items, pagination: paginationMeta } = normalizeListResponse(response.data);

            // Update IndexedDB cache with fresh data
            try {
              await saveDropdownCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.error('EditTasks: Error saving projects to cache:', saveError);
            }

            // Update UI with fresh data
            setProjectOptions(items);
            setProjectPagination(paginationMeta);
          } catch (apiError) {
            // If API fails and we have cached data, keep using it
            if (!cachedData || !cachedData.items || !Array.isArray(cachedData.items) || cachedData.items.length === 0) {
              toast.error(apiError?.response?.data?.message || t('tasks.edit.messages.fetchProjectsError'), { rtl: isRTL });
              setProjectOptions([]);
              setProjectPagination(null);
            } else {
              toast.warning(
                isRTL
                  ? 'لا يمكن الاتصال بالخادم. يتم عرض البيانات المحفوظة محلياً.'
                  : 'Cannot connect to server. Showing locally saved data.',
                { rtl: isRTL, autoClose: 3000 }
              );
            }
          }
        } else {
          // Offline: use cached data if available
          if (!cachedData || !cachedData.items || !Array.isArray(cachedData.items) || cachedData.items.length === 0) {
            toast.warning(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة.'
                : 'No internet connection and no locally saved data available.',
              { rtl: isRTL, autoClose: 3000 }
            );
            setProjectOptions([]);
            setProjectPagination(null);
          } else {
            toast.info(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت. يتم عرض البيانات المحفوظة محلياً.'
                : 'No internet connection. Showing locally saved data.',
              { rtl: isRTL, autoClose: 3000 }
            );
          }
        }
      } catch (error) {
        console.error('EditTasks: Error in fetchProjects:', error);
        toast.error(error?.response?.data?.message || t('tasks.edit.messages.fetchProjectsError'), { rtl: isRTL });
        setProjectOptions([]);
        setProjectPagination(null);
      }
    }, [dispatch, t, i18n, isRTL]);

  // Fetch Employees (with offline cache support)

  const fetchEmployees = useCallback(
    async (page = employeePage, search = employeeSearchTerm) => {
      try {
        // Generate cache key
        const cacheKey = generateDropdownCacheKey('employees', {
          page,
          per_page: 10,
          search: search || '',
          language: i18n.language,
        });

        // Step 1: Try to load from IndexedDB first (Source of Truth)
        let cachedData = null;
        try {
          cachedData = await getDropdownCache(cacheKey);
          if (cachedData && cachedData.items && Array.isArray(cachedData.items)) {
            setEmployeeOptions(cachedData.items);
            setEmployeePagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.error('EditTasks: Error loading employees from cache:', cacheError);
        }

        // Step 2: Try to fetch from API (for sync/update)
        if (isOnline()) {
          try {
            const payload = await dispatch(
              getEmployees({
                page,
                per_page: 10,
                search: search || undefined,
                language: i18n.language,
              })
            ).unwrap();
            const { items, pagination: paginationMeta } = normalizeListResponse(payload);

            // Update IndexedDB cache with fresh data
            try {
              await saveDropdownCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.error('EditTasks: Error saving employees to cache:', saveError);
            }

            // Update UI with fresh data
            setEmployeeOptions(items);
            setEmployeePagination(paginationMeta);
          } catch (apiError) {
            // If API fails and we have cached data, keep using it
            if (!cachedData || !cachedData.items || !Array.isArray(cachedData.items) || cachedData.items.length === 0) {
              toast.error(apiError || t('tasks.edit.messages.fetchEmployeesError'), { rtl: isRTL });
              setEmployeeOptions([]);
              setEmployeePagination(null);
            } else {
              toast.warning(
                isRTL
                  ? 'لا يمكن الاتصال بالخادم. يتم عرض البيانات المحفوظة محلياً.'
                  : 'Cannot connect to server. Showing locally saved data.',
                { rtl: isRTL, autoClose: 3000 }
              );
            }
          }
        } else {
          // Offline: use cached data if available
          if (!cachedData || !cachedData.items || !Array.isArray(cachedData.items) || cachedData.items.length === 0) {
            toast.warning(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة.'
                : 'No internet connection and no locally saved data available.',
              { rtl: isRTL, autoClose: 3000 }
            );
            setEmployeeOptions([]);
            setEmployeePagination(null);
          } else {
            toast.info(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت. يتم عرض البيانات المحفوظة محلياً.'
                : 'No internet connection. Showing locally saved data.',
              { rtl: isRTL, autoClose: 3000 }
            );
          }
        }
      } catch (error) {
        console.error('EditTasks: Error in fetchEmployees:', error);
        toast.error(error || t('tasks.edit.messages.fetchEmployeesError'), { rtl: isRTL });
        setEmployeeOptions([]);
        setEmployeePagination(null);
      }
    },
    [dispatch, t, i18n, isRTL]
  );

  // Fetch Lab Tests (with offline cache support)

  const fetchLabTests = useCallback(
    async (page = labPage, search = labSearchTerm) => {
      try {
        // Generate cache key
        const cacheKey = generateDropdownCacheKey('lab_tests', {
          page,
          per_page: 10,
          search: search || '',
        });

        // Step 1: Try to load from IndexedDB first (Source of Truth)
        let cachedData = null;
        try {
          cachedData = await getDropdownCache(cacheKey);
          if (cachedData && cachedData.items && Array.isArray(cachedData.items)) {
            setLabOptions(cachedData.items);
            setLabPagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.error('EditTasks: Error loading lab tests from cache:', cacheError);
        }

        // Step 2: Try to fetch from API (for sync/update)
        if (isOnline()) {
          try {
            const payload = await dispatch(
              getLabTests({
                page,
                per_page: 10,
                search: search || undefined,
              })
            ).unwrap();
            const { items, pagination: paginationMeta } = normalizeListResponse(payload);

            // Update IndexedDB cache with fresh data
            try {
              await saveDropdownCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.error('EditTasks: Error saving lab tests to cache:', saveError);
            }

            // Update UI with fresh data
            setLabOptions(items);
            setLabPagination(paginationMeta);
          } catch (apiError) {
            // If API fails and we have cached data, keep using it
            if (!cachedData || !cachedData.items || !Array.isArray(cachedData.items) || cachedData.items.length === 0) {
              toast.error(apiError || t('tasks.edit.messages.fetchLabTestsError'), { rtl: isRTL });
              setLabOptions([]);
              setLabPagination(null);
            } else {
              toast.warning(
                isRTL
                  ? 'لا يمكن الاتصال بالخادم. يتم عرض البيانات المحفوظة محلياً.'
                  : 'Cannot connect to server. Showing locally saved data.',
                { rtl: isRTL, autoClose: 3000 }
              );
            }
          }
        } else {
          // Offline: use cached data if available
          if (!cachedData || !cachedData.items || !Array.isArray(cachedData.items) || cachedData.items.length === 0) {
            toast.warning(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة.'
                : 'No internet connection and no locally saved data available.',
              { rtl: isRTL, autoClose: 3000 }
            );
            setLabOptions([]);
            setLabPagination(null);
          } else {
            toast.info(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت. يتم عرض البيانات المحفوظة محلياً.'
                : 'No internet connection. Showing locally saved data.',
              { rtl: isRTL, autoClose: 3000 }
            );
          }
        }
      } catch (error) {
        console.error('EditTasks: Error in fetchLabTests:', error);
        toast.error(error || t('tasks.edit.messages.fetchLabTestsError'), { rtl: isRTL });
        setLabOptions([]);
        setLabPagination(null);
      }
    },
    [dispatch, labPage, labSearchTerm, t, isRTL]
  );

  useEffect(() => {
    if (projectDropdownOpen) {
      fetchProjects(projectPage, projectSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDropdownOpen, projectPage, projectSearchTerm]);

  useEffect(() => {
    if (employeeDropdownOpen) {
      fetchEmployees(employeePage, employeeSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeDropdownOpen, employeePage, employeeSearchTerm]);

  useEffect(() => {
    if (labDropdownOpen) {
      fetchLabTests(labPage, labSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labDropdownOpen, labPage, labSearchTerm]);

  useEffect(() => {
    if (projectSearchTerm !== undefined && projectDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setProjectPage(1);
        fetchProjects(1, projectSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSearchTerm, projectDropdownOpen]);

  useEffect(() => {
    if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEmployeePage(1);
        fetchEmployees(1, employeeSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [employeeSearchTerm, employeeDropdownOpen]);

  useEffect(() => {
    if (labSearchTerm !== undefined && labDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setLabPage(1);
        fetchLabTests(1, labSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [labSearchTerm, labDropdownOpen]);

  // Close dropdowns when clicking outside or opening another dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownContainer = target.closest('[data-dropdown-container]');
      const isDropdownToggle = target.closest('[data-dropdown-toggle]');
      // Check if click is on the add test button
      const isAddTestButton = target.closest('[data-add-test-button]');
      // Check if click is on the add project button
      const isAddProjectButton = target.closest('[data-add-project-button]');
      // Check if click is on the modal overlay or modal content
      const isModal = target.closest('[data-modal-overlay]') || target.closest('[data-modal-content]');

      // Never open modal from this handler - only close dropdowns
      // Only close dropdowns if clicking outside, not on add button, and not on modal
      if (!isDropdownContainer && !isDropdownToggle && !isAddTestButton && !isAddProjectButton && !isModal) {
        if (projectDropdownOpen) setProjectDropdownOpen(false);
        if (employeeDropdownOpen) setEmployeeDropdownOpen(false);
        if (labDropdownOpen) setLabDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [projectDropdownOpen, employeeDropdownOpen, labDropdownOpen]);

  // Close other dropdowns when opening one
  useEffect(() => {
    if (projectDropdownOpen) {
      if (employeeDropdownOpen) setEmployeeDropdownOpen(false);
      if (labDropdownOpen) setLabDropdownOpen(false);
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    if (employeeDropdownOpen) {
      if (projectDropdownOpen) setProjectDropdownOpen(false);
      if (labDropdownOpen) setLabDropdownOpen(false);
    }
  }, [employeeDropdownOpen]);

  useEffect(() => {
    if (labDropdownOpen) {
      if (projectDropdownOpen) setProjectDropdownOpen(false);
      if (employeeDropdownOpen) setEmployeeDropdownOpen(false);
    }
  }, [labDropdownOpen]);

  // Project modal dropdowns useEffect hooks
  useEffect(() => {
    if (customerDropdownOpen) {
      dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined })).then((result) => {
        if (result.payload?.data) {
          const { items, pagination } = normalizeListResponse(result.payload);
          setCustomers(items);
          setCustomersPagination(
            pagination ? { ...pagination, current_page: customerCurrentPage } : pagination
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCurrentPage, customerDropdownOpen, customerSearchTerm]);

  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined })).then((result) => {
          if (result.payload?.data) {
            const { items, pagination } = normalizeListResponse(result.payload);
            setCustomers(items);
            setCustomersPagination(
              pagination ? { ...pagination, current_page: 1 } : pagination
            );
          }
        });
      }, 300);
    }
    return () => {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchTerm, customerDropdownOpen]);

  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined })).then((result) => {
        if (result.payload?.data) {
          const { items, pagination } = normalizeListResponse(result.payload);
          setTaxes(items);
          setTaxesPagination(
            pagination ? { ...pagination, current_page: taxCurrentPage } : pagination
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxCurrentPage, taxDropdownOpen, taxSearchTerm]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined })).then((result) => {
          if (result.payload?.data) {
            const { items, pagination } = normalizeListResponse(result.payload);
            setTaxes(items);
            setTaxesPagination(
              pagination ? { ...pagination, current_page: 1 } : pagination
            );
          }
        });
      }, 300);
    }
    return () => {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxSearchTerm, taxDropdownOpen]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined })).then((result) => {
        if (result.payload?.data) {
          const { items, pagination } = normalizeListResponse(result.payload);
          setCostCenters(items);
          setCostCentersPagination(
            pagination ? { ...pagination, current_page: costCenterCurrentPage } : pagination
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterCurrentPage, costCenterDropdownOpen, costCenterSearchTerm]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined })).then((result) => {
          if (result.payload?.data) {
            const { items, pagination } = normalizeListResponse(result.payload);
            setCostCenters(items);
            setCostCentersPagination(
              pagination ? { ...pagination, current_page: 1 } : pagination
            );
          }
        });
      }, 300);
    }
    return () => {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterSearchTerm, costCenterDropdownOpen]);

  // Close other dropdowns when opening one in project modal
  useEffect(() => {
    if (customerDropdownOpen) {
      setTaxDropdownOpen(false);
      setCostCenterDropdownOpen(false);
    }
  }, [customerDropdownOpen]);

  useEffect(() => {
    if (taxDropdownOpen) {
      setCustomerDropdownOpen(false);
      setCostCenterDropdownOpen(false);
    }
  }, [taxDropdownOpen]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      setCustomerDropdownOpen(false);
      setTaxDropdownOpen(false);
    }
  }, [costCenterDropdownOpen]);

  // Close dropdowns when clicking outside project modal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showAddProjectModal) {
        if (customerDropdownOpen && !e.target.closest('[data-dropdown-container]') && !e.target.closest('[data-dropdown-toggle]')) setCustomerDropdownOpen(false);
        if (taxDropdownOpen && !e.target.closest('[data-dropdown-container]') && !e.target.closest('[data-dropdown-toggle]')) setTaxDropdownOpen(false);
        if (costCenterDropdownOpen && !e.target.closest('[data-dropdown-container]') && !e.target.closest('[data-dropdown-toggle]')) setCostCenterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddProjectModal, customerDropdownOpen, taxDropdownOpen, costCenterDropdownOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLabToggle = (item) => {
    setForm((prev) => {
      const exists = prev.lab_test_ids.includes(String(item.id));
      const updatedIds = exists
        ? prev.lab_test_ids.filter((id) => id !== String(item.id))
        : [...prev.lab_test_ids, String(item.id)];
      return { ...prev, lab_test_ids: updatedIds };
    });
    // Don't close dropdown when selecting - allow multiple selections
  };

  const handleAddTest = async (e) => {
    e.preventDefault();
    if (!testForm.name_ar || !testForm.name_en) {
      toast.error(t('tasks.edit.messages.testNameRequired', { defaultValue: 'برجاء إدخال اسم الاختبار' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingTest(true);

      // Create FormData for offline support
      const formDataObj = new FormData();
      formDataObj.append('name_ar', testForm.name_ar);
      formDataObj.append('name_en', testForm.name_en);
      formDataObj.append('is_active', testForm.is_active);

      try {
        // Try to use offline request manager first
        const result = await makeOfflineRequest('/tests', 'POST', formDataObj);
        const newTest = result?.data || result;

        if (newTest?.id) {
          // Add to form
          setForm((prev) => ({
            ...prev,
            lab_test_ids: [...prev.lab_test_ids, String(newTest.id)],
          }));
          // Refresh lab tests list
          await fetchLabTests(labPage, labSearchTerm);
          // Close modal and reset form
          setShowAddTestModal(false);
          setTestForm({
            name_ar: '',
            name_en: '',
            is_active: true,
          });
          toast.success(t('tasks.edit.messages.testAdded', { defaultValue: 'تم إضافة الاختبار بنجاح' }), { rtl: isRTL });
        }
      } catch (offlineError) {
        // If request was saved offline, show appropriate message
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          // Close modal and reset form
          setShowAddTestModal(false);
          setTestForm({
            name_ar: '',
            name_en: '',
            is_active: true,
          });
          return;
        }

        // If it's a different error, try the original axios call as fallback
        const token = getToken();
        const currentLang = i18n.language || 'ar';
        const response = await axios.post(
          `${BASE_URL}/tests`,
          {
            name_ar: testForm.name_ar,
            name_en: testForm.name_en,
            is_active: testForm.is_active,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept-Language': currentLang,
            },
          }
        );
        const newTest = response.data?.data || response.data;
        if (newTest?.id) {
          // Add to form
          setForm((prev) => ({
            ...prev,
            lab_test_ids: [...prev.lab_test_ids, String(newTest.id)],
          }));
          // Refresh lab tests list
          await fetchLabTests(labPage, labSearchTerm);
          // Close modal and reset form
          setShowAddTestModal(false);
          setTestForm({
            name_ar: '',
            name_en: '',
            is_active: true,
          });
          toast.success(t('tasks.edit.messages.testAdded', { defaultValue: 'تم إضافة الاختبار بنجاح' }), { rtl: isRTL });
        }
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('tasks.edit.messages.testAddError', { defaultValue: 'فشل إضافة الاختبار' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingTest(false);
    }
  };

  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTaxFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCostCenterFormChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomerFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.name_ar || !customerForm.name_en) {
      toast.error(t('quotations.add.modals.addCustomer.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية والإنجليزية' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCustomer(true);
      const formDataObj = new FormData();
      formDataObj.append('name_en', customerForm.name_en);
      formDataObj.append('name_ar', customerForm.name_ar);
      if (customerForm.code) formDataObj.append('code', customerForm.code);
      if (customerForm.contact_person) formDataObj.append('contact_person', customerForm.contact_person);
      if (customerForm.email) formDataObj.append('email', customerForm.email);
      if (customerForm.phone) formDataObj.append('phone', customerForm.phone);
      if (customerForm.address) formDataObj.append('address', customerForm.address);
      if (customerForm.tax_number) formDataObj.append('tax_number', customerForm.tax_number);
      formDataObj.append('is_active', customerForm.is_active ? '1' : '0');
      if (customerForm.notes) formDataObj.append('notes', customerForm.notes);

      try {
        const result = await makeOfflineRequest('/customers', 'POST', formDataObj);
        if (result?.data?.id) {
          setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
          await dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined })).then((result) => {
            if (result.payload?.data) {
              const { items, pagination } = normalizeListResponse(result.payload);
              setCustomers(items);
              setCustomersPagination(
                pagination ? { ...pagination, current_page: customerCurrentPage } : pagination
              );
            }
          });
          setShowAddCustomerModal(false);
          setCustomerForm({ name_en: '', name_ar: '', code: '', contact_person: '', email: '', phone: '', address: '', tax_number: '', is_active: true, notes: '' });
          toast.success(result?.message || t('quotations.add.modals.addCustomer.success', { defaultValue: 'تم إضافة العميل بنجاح' }), { rtl: isRTL });
        }
      } catch (offlineError) {
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          setShowAddCustomerModal(false);
          setCustomerForm({ name_en: '', name_ar: '', code: '', contact_person: '', email: '', phone: '', address: '', tax_number: '', is_active: true, notes: '' });
          return;
        }
        await dispatch(addCustomer({ ...customerForm, is_active: customerForm.is_active ? true : false })).unwrap();
        toast.success(t('quotations.add.modals.addCustomer.success', { defaultValue: 'تم إضافة العميل بنجاح' }), { rtl: isRTL });
        setShowAddCustomerModal(false);
        setCustomerForm({ name_en: '', name_ar: '', code: '', contact_person: '', email: '', phone: '', address: '', tax_number: '', is_active: true, notes: '' });
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('quotations.add.modals.addCustomer.error', { defaultValue: 'فشل إضافة العميل' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingCustomer(false);
    }
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    if (!taxForm.name_ar || !taxForm.name_en || taxForm.rate === '') {
      toast.error(t('quotations.add.modals.addTax.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingTax(true);
      const formDataObj = new FormData();
      formDataObj.append('name_ar', taxForm.name_ar);
      formDataObj.append('name_en', taxForm.name_en);
      formDataObj.append('rate', Number(taxForm.rate));
      formDataObj.append('is_active', Boolean(taxForm.is_active) ? '1' : '0');

      try {
        const result = await makeOfflineRequest('/taxes', 'POST', formDataObj);
        if (result?.data?.id) {
          setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
          await dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined })).then((result) => {
            if (result.payload?.data) {
              const { items, pagination } = normalizeListResponse(result.payload);
              setTaxes(items);
              setTaxesPagination(
                pagination ? { ...pagination, current_page: taxCurrentPage } : pagination
              );
            }
          });
          setShowAddTaxModal(false);
          setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
          toast.success(result?.message || t('quotations.add.modals.addTax.success', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
        }
      } catch (offlineError) {
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          setShowAddTaxModal(false);
          setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
          return;
        }
        await dispatch(addTax({ name_ar: taxForm.name_ar, name_en: taxForm.name_en, rate: Number(taxForm.rate), is_active: Boolean(taxForm.is_active) })).unwrap();
        toast.success(t('quotations.add.modals.addTax.success', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
        setShowAddTaxModal(false);
        setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('quotations.add.modals.addTax.error', { defaultValue: 'فشل إضافة الضريبة' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingTax(false);
    }
  };

  const handleAddCostCenter = async (e) => {
    e.preventDefault();
    if (!costCenterForm.name_ar || !costCenterForm.name_en) {
      toast.error(t('quotations.add.modals.addCostCenter.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCostCenter(true);
      const formDataObj = new FormData();
      formDataObj.append('name_ar', costCenterForm.name_ar);
      formDataObj.append('name_en', costCenterForm.name_en);
      if (costCenterForm.code) {
        formDataObj.append('code', costCenterForm.code);
      }

      try {
        const result = await makeOfflineRequest('/cost-centers', 'POST', formDataObj);
        if (result?.data?.id) {
          setProjectForm(prev => ({ ...prev, cost_center_id: result.data.id }));
          await dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined })).then((result) => {
            if (result.payload?.data) {
              const { items, pagination } = normalizeListResponse(result.payload);
              setCostCenters(items);
              setCostCentersPagination(
                pagination ? { ...pagination, current_page: costCenterCurrentPage } : pagination
              );
            }
          });
          setShowAddCostCenterModal(false);
          setCostCenterForm({ name_ar: '', name_en: '', code: '' });
          toast.success(result?.message || t('quotations.add.modals.addCostCenter.success', { defaultValue: 'تم إضافة مركز التكلفة بنجاح' }), { rtl: isRTL });
        }
      } catch (offlineError) {
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          setShowAddCostCenterModal(false);
          setCostCenterForm({ name_ar: '', name_en: '', code: '' });
          return;
        }
        await dispatch(addCostCenter({ name_ar: costCenterForm.name_ar, name_en: costCenterForm.name_en, code: costCenterForm.code || undefined })).unwrap();
        toast.success(t('quotations.add.modals.addCostCenter.success', { defaultValue: 'تم إضافة مركز التكلفة بنجاح' }), { rtl: isRTL });
        setShowAddCostCenterModal(false);
        setCostCenterForm({ name_ar: '', name_en: '', code: '' });
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('quotations.add.modals.addCostCenter.error', { defaultValue: 'فشل إضافة مركز التكلفة' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingCostCenter(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!projectForm.name_ar) {
      toast.error(t('quotations.add.modals.addProject.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingProject(true);
      // Create FormData for offline support
      const formDataObj = new FormData();
      if (projectForm.code) formDataObj.append('code', projectForm.code);
      formDataObj.append('name_ar', projectForm.name_ar);
      if (projectForm.name_en) formDataObj.append('name_en', projectForm.name_en);
      if (projectForm.customer_id) formDataObj.append('customer_id', projectForm.customer_id);
      if (projectForm.start_date) formDataObj.append('start_date', projectForm.start_date);
      if (projectForm.end_date) formDataObj.append('end_date', projectForm.end_date);
      formDataObj.append('status', projectForm.status);
      if (projectForm.contract_value) formDataObj.append('contract_value', projectForm.contract_value);
      if (projectForm.tax_id) formDataObj.append('tax_id', projectForm.tax_id);
      if (projectForm.cost_center_id) formDataObj.append('cost_center_id', projectForm.cost_center_id);
      if (projectForm.notes) formDataObj.append('notes', projectForm.notes);

      try {
        // Try to use offline request manager first
        const result = await makeOfflineRequest('/projects', 'POST', formDataObj);
        const newProject = result?.data || result;

        if (newProject?.id) {
          // Refresh projects list
          await fetchProjects(projectPage, projectSearchTerm);
          // Set the newly added project
          setForm((prev) => ({ ...prev, project_id: String(newProject.id) }));
          setShowAddProjectModal(false);
          setProjectForm({
            code: '',
            name_ar: '',
            name_en: '',
            customer_id: null,
            start_date: '',
            end_date: '',
            status: 'planned',
            contract_value: '',
            tax_id: null,
            cost_center_id: null,
            notes: '',
          });
          toast.success(t('quotations.add.modals.addProject.success', { defaultValue: 'تم إضافة المشروع بنجاح' }), { rtl: isRTL });
        }
      } catch (offlineError) {
        // If request was saved offline, show appropriate message
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          // Close modal and reset form
          setShowAddProjectModal(false);
          setProjectForm({
            code: '',
            name_ar: '',
            name_en: '',
            customer_id: null,
            start_date: '',
            end_date: '',
            status: 'planned',
            contract_value: '',
            tax_id: null,
            cost_center_id: null,
            notes: '',
          });
          return;
        }

        // If it's a different error, try the original Redux dispatch as fallback
        const payload = {
          code: projectForm.code,
          name_ar: projectForm.name_ar,
          name_en: projectForm.name_en || undefined,
          customer_id: projectForm.customer_id || null,
          start_date: projectForm.start_date || undefined,
          end_date: projectForm.end_date || undefined,
          status: projectForm.status,
          contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
          tax_id: projectForm.tax_id || null,
          cost_center_id: projectForm.cost_center_id || null,
          notes: projectForm.notes || undefined,
        };
        const result = await dispatch(addProject(payload)).unwrap();
        if (result?.data?.id) {
          // Refresh projects list
          await fetchProjects(projectPage, projectSearchTerm);
          // Set the newly added project
          setForm((prev) => ({ ...prev, project_id: String(result.data.id) }));
          setShowAddProjectModal(false);
          setProjectForm({
            code: '',
            name_ar: '',
            name_en: '',
            customer_id: null,
            start_date: '',
            end_date: '',
            status: 'planned',
            contract_value: '',
            tax_id: null,
            cost_center_id: null,
            notes: '',
          });
          toast.success(t('quotations.add.modals.addProject.success', { defaultValue: 'تم إضافة المشروع بنجاح' }), { rtl: isRTL });
        }
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('quotations.add.modals.addProject.error', { defaultValue: 'فشل إضافة المشروع' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const renderDropdownItem = (item, codeKey, nameArKey, nameEnKey) => {
    const code = item[codeKey];
    const nameAr = item[nameArKey];
    const nameEn = item[nameEnKey];
    const displayName = isRTL ? (nameAr || nameEn || '') : (nameEn || nameAr || '');
    return code ? `${code} - ${displayName}` : displayName;
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const mapped = files.map((file) => ({
      id: `${Date.now()}-${file.name}-${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
    }));

    setNewAttachments((prev) => [...prev, ...mapped]);
    event.target.value = '';
  };

  const removeNewAttachment = (id) => {
    setNewAttachments((prev) => prev.filter((file) => file.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) {
      toast.error(t('tasks.edit.messages.projectRequired'), { rtl: isRTL });
      return;
    }
    if (!form.name_ar || !form.name_en) {
      toast.error(t('tasks.edit.messages.nameRequired'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmitting(true);
      let taskData;
      let useFormData = newAttachments.length > 0;
      if (useFormData) {
        taskData = new FormData();
        taskData.append('project_id', Number(form.project_id));
        taskData.append('name_ar', form.name_ar);
        taskData.append('name_en', form.name_en);
        taskData.append('status', form.status);
        taskData.append('priority', form.priority);
        if (form.description) taskData.append('description', form.description);
        if (form.start_date) taskData.append('start_date', form.start_date);
        if (form.end_date) taskData.append('end_date', form.end_date);
        if (form.notes) taskData.append('notes', form.notes);
        if (form.employee_id) taskData.append('employee_id', Number(form.employee_id));
        if (Array.isArray(form.lab_test_ids) && form.lab_test_ids.length > 0) {
          form.lab_test_ids.forEach((id) => taskData.append('lab_test_ids[]', Number(id)));
        }
        newAttachments.forEach((fileWrapper) => taskData.append('files[]', fileWrapper.file));
      } else {
        taskData = {
          project_id: Number(form.project_id),
          name_ar: form.name_ar,
          name_en: form.name_en,
          status: form.status,
          priority: form.priority,
          description: form.description || undefined,
          start_date: form.start_date || undefined,
          end_date: form.end_date || undefined,
          notes: form.notes || undefined,
          employee_id: form.employee_id ? Number(form.employee_id) : undefined,
          lab_test_ids: Array.isArray(form.lab_test_ids) && form.lab_test_ids.length > 0 ? form.lab_test_ids.map(Number) : [],
        };
      }
      try {
        // جرب الحفظ أوفلاين أولاً
        const result = await makeOfflineRequest(`/tasks/${id}`, 'PUT', taskData);
        toast.success(t('tasks.edit.messages.updateSuccess'), { rtl: isRTL });
        navigate('/tasks');
      } catch (offlineError) {
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          navigate('/tasks');
          return;
        }
        // إذا فشل الأوفلاين جرب الريداكس
        await dispatch(updateTask({ id, taskData })).unwrap();
        toast.success(t('tasks.edit.messages.updateSuccess'), { rtl: isRTL });
        navigate('/tasks');
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('tasks.edit.messages.updateError', { defaultValue: 'فشل تعديل المهمة' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedProject = () => {
    if (!form.project_id) return null;
    return projectOptions.find((p) => String(p.id) === String(form.project_id));
  };

  const getSelectedEmployee = () => {
    if (!form.employee_id) return null;
    return employeeOptions.find((e) => String(e.id) === String(form.employee_id));
  };

  const resolveLabTestLabel = (id) => {
    const match = labOptions.find((test) => String(test.id) === String(id));
    if (match) {
      return isRTL ? match.name_ar || match.name_en || `#${match.id}` : match.name_en || match.name_ar || `#${match.id}`;
    }
    return `#${id}`;
  };

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={cardStyle}>{t('tasks.edit.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>{t('tasks.edit.title')}</h2>
          <button type="button" onClick={() => navigate('/tasks')} style={{ backgroundColor: '#666', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '14px' }}>
            {t('btns.back', { defaultValue: 'Back' })}
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('tasks.edit.project')} *</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <div style={{ position: 'relative', flex: 1 }} data-dropdown-container>
                  <div
                    data-dropdown-toggle
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setProjectDropdownOpen((prev) => !prev);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    style={dropdownDisplayStyle}
                  >
                    <span>
                      {getSelectedProject()
                        ? `${getSelectedProject().code ? `${getSelectedProject().code} - ` : ''}${isRTL
                          ? (getSelectedProject().name_ar || getSelectedProject().name_en || '')
                          : (getSelectedProject().name_en || getSelectedProject().name_ar || '')}`
                        : t('tasks.edit.selectProject')}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  <DropdownWithSearch
                    isOpen={projectDropdownOpen}
                    searchTerm={projectSearchTerm}
                    onSearchChange={setProjectSearchTerm}
                    searchInputRef={projectSearchInputRef}
                    items={projectOptions}
                    pagination={projectPagination}
                    onPageChange={setProjectPage}
                    onClose={() => setProjectDropdownOpen(false)}
                    onSelect={(item) => {
                      setForm((prev) => ({ ...prev, project_id: String(item.id) }));
                      setProjectDropdownOpen(false);
                      setProjectSearchTerm('');
                      setProjectPage(1);
                    }}
                    label={t('tasks.edit.project')}
                  />
                </div>
                <button
                  type="button"
                  data-add-project-button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowAddProjectModal(true);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '44px',
                    height: '44px'
                  }}
                  title={t('tasks.add.addProject', { defaultValue: 'إضافة مشروع جديد' })}
                >
                  <FaPlus />
                </button>
              </div>
            </label>
          </div>

          <div style={gridStyle}>
            <Input label={t('tasks.edit.taskNameAr')} name="name_ar" value={form.name_ar} onChange={handleChange} required isRTL={isRTL} />
            <Input label={t('tasks.edit.taskNameEn')} name="name_en" value={form.name_en} onChange={handleChange} required isRTL={isRTL} />
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('tasks.edit.description')}</span>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                resize: 'vertical',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </label>

          <div style={gridStyle}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 14 }}>{t('tasks.edit.status')}</span>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                style={{ ...filterInputStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                required
              >
                <option value="pending">{t('tasks.statuses.pending')}</option>
                <option value="in_progress">{t('tasks.statuses.in_progress')}</option>
                <option value="completed">{t('tasks.statuses.completed')}</option>
                <option value="on_hold">{t('tasks.statuses.on_hold')}</option>
                <option value="cancelled">{t('tasks.statuses.cancelled')}</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 14 }}>{t('tasks.edit.priority')}</span>
              <select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                style={{ ...filterInputStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                required
              >
                <option value="low">{t('tasks.priorities.low')}</option>
                <option value="medium">{t('tasks.priorities.medium')}</option>
                <option value="high">{t('tasks.priorities.high')}</option>
              </select>
            </label>
          </div>

          <div style={gridStyle}>
            <Input label={t('tasks.edit.startDate')} type="date" name="start_date" value={form.start_date} onChange={handleChange} isRTL={isRTL} />
            <Input label={t('tasks.edit.endDate')} type="date" name="end_date" value={form.end_date} onChange={handleChange} isRTL={isRTL} />
          </div>

          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('tasks.add.employee')}</span>
              <div data-dropdown-toggle onClick={() => setEmployeeDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                <span>
                  {getSelectedEmployee()
                    ? `${getSelectedEmployee().employee_code ? `${getSelectedEmployee().employee_code} - ` : ''}${getSelectedEmployee().name || ''}`
                    : t('tasks.add.selectEmployee')}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              <DropdownWithSearch
                isOpen={employeeDropdownOpen}
                searchTerm={employeeSearchTerm}
                onSearchChange={setEmployeeSearchTerm}
                searchInputRef={employeeSearchInputRef}
                items={employeeOptions}
                pagination={employeePagination}
                onPageChange={setEmployeePage}
                onClose={() => setEmployeeDropdownOpen(false)}
                onSelect={(item) => {
                  setForm((prev) => ({ ...prev, employee_id: String(item.id) }));
                  setEmployeeDropdownOpen(false);
                  setEmployeeSearchTerm('');
                  setEmployeePage(1);
                }}
                label={t('tasks.add.employee')}
                isRTL={isRTL}
              />
            </label>
          </div>

          <div style={{ position: 'relative' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('tasks.labTests.label')}</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <div style={{ position: 'relative', flex: 1 }} data-dropdown-container>
                  <div
                    data-dropdown-toggle
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Make absolutely sure we're not clicking on add button
                      const clickedElement = e.target;
                      const isAddButton = clickedElement.closest('[data-add-test-button]') ||
                        clickedElement.closest('button[data-add-test-button]') ||
                        clickedElement === document.querySelector('[data-add-test-button]');
                      if (!isAddButton) {
                        // Only toggle dropdown, never open modal
                        setLabDropdownOpen((prev) => !prev);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Make absolutely sure we're not clicking on add button
                      const clickedElement = e.target;
                      const isAddButton = clickedElement.closest('[data-add-test-button]') ||
                        clickedElement.closest('button[data-add-test-button]');
                      if (isAddButton) {
                        e.stopPropagation();
                        e.preventDefault();
                        return false;
                      }
                    }}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', userSelect: 'none', pointerEvents: 'auto' }}
                  >
                    <span>{form.lab_test_ids.length > 0 ? t('tasks.labTests.selectedCount', { count: form.lab_test_ids.length }) : t('tasks.labTests.selectPlaceholder')}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  <DropdownWithSearchMultiple
                    isOpen={labDropdownOpen}
                    searchTerm={labSearchTerm}
                    onSearchChange={setLabSearchTerm}
                    searchInputRef={labSearchInputRef}
                    items={labOptions}
                    pagination={labPagination}
                    onPageChange={setLabPage}
                    onToggleSelect={handleLabToggle}
                    onClose={() => {
                      // Only close when clicking outside - handled by handleClickOutside
                    }}
                    label={t('tasks.labTests.label')}
                    selectedIds={form.lab_test_ids}
                    isRTL={isRTL}
                  />
                </div>
                <button
                  type="button"
                  data-add-test-button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // Only open modal if clicking directly on the button or its children (like FaPlus icon)
                    const isDirectClick = e.target === e.currentTarget ||
                      e.currentTarget.contains(e.target) ||
                      e.target.closest('[data-add-test-button]') === e.currentTarget;
                    if (isDirectClick) {
                      setShowAddTestModal(true);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // Prevent any event from reaching dropdown toggle
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '44px',
                    height: '44px',
                    zIndex: 10,
                    isolation: 'isolate'
                  }}
                  title={t('tasks.add.addTest', { defaultValue: 'إضافة اختبار جديد' })}
                >
                  <FaPlus />
                </button>
              </div>
            </label>
            {form.lab_test_ids.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: -8 }}>
                {form.lab_test_ids.map((id) => (
                  <span key={id} style={{ backgroundColor: '#AC2000', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {resolveLabTestLabel(id)}
                    <button
                      type="button"
                      onClick={() => handleLabToggle({ id })}
                      style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12, padding: 0 }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 14 }}>{t('tasks.attachments.label')}</span>
          </label>
          {existingAttachments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 14 }}>{t('tasks.attachments.current')}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {existingAttachments.map((file) => (
                  <a
                    key={file.id}
                    href={file.url || '#'}
                    target="_blank"
                    rel="noreferrer"
                    style={{ backgroundColor: '#2a2f3f', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 12, textDecoration: 'none' }}
                  >
                    {file.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('tasks.attachments.addNew')}</span>
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
              }}
            />
            {newAttachments.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {newAttachments.map((file) => (
                  <span key={file.id} style={{ backgroundColor: '#2a2f3f', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {file.name}
                    <button
                      type="button"
                      onClick={() => removeNewAttachment(file.id)}
                      style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12, padding: 0 }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('tasks.edit.notes')}</span>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={4}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                resize: 'vertical',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </label>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
            <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
              {isSubmitting ? t('tasks.edit.saving') : t('tasks.edit.save')}
            </button>
            <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/tasks')}>
              {t('tasks.edit.cancel')}
            </button>
          </div>
        </form>
      </div>

      {/* Modal لإضافة اختبار جديد */}
      {showAddTestModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999,
          padding: '20px'
        }} onClick={() => setShowAddTestModal(false)}>
          <div style={{
            backgroundColor: '#1b3350',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid #333',
            direction: isRTL ? 'rtl' : 'ltr'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.add.addTest', { defaultValue: 'إضافة اختبار جديد' })}</h2>
              <button
                onClick={() => {
                  setShowAddTestModal(false);
                  setTestForm({
                    name_ar: '',
                    name_en: '',
                    is_active: true,
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTest}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.add.testNameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_ar" value={testForm.name_ar} onChange={(e) => setTestForm(prev => ({ ...prev, name_ar: e.target.value }))} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.add.testNameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_en" value={testForm.name_en} onChange={(e) => setTestForm(prev => ({ ...prev, name_en: e.target.value }))} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.add.status', { defaultValue: 'الحالة' })}</label>
                  <select name="is_active" value={testForm.is_active ? 'true' : 'false'} onChange={(e) => setTestForm(prev => ({ ...prev, is_active: e.target.value === 'true' }))} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="true">{t('tasks.add.active', { defaultValue: 'نشط' })}</option>
                    <option value="false">{t('tasks.add.inactive', { defaultValue: 'غير نشط' })}</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={isSubmittingTest}
                  style={{
                    backgroundColor: isSubmittingTest ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: isSubmittingTest ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmittingTest ? 0.6 : 1
                  }}
                >
                  {isSubmittingTest ? t('tasks.edit.saving') : t('tasks.edit.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTestModal(false);
                    setTestForm({
                      name_ar: 'اختبار جديد',
                      name_en: 'New Test',
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('tasks.edit.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal لإضافة مشروع جديد */}
      {showAddProjectModal && createPortal(
        <div
          data-modal-overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddProjectModal(false);
            }
          }}
        >
          <div
            data-modal-content
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddProjectModal(false);
                  setProjectForm({
                    code: '',
                    name_ar: '',
                    name_en: '',
                    customer_id: null,
                    start_date: '',
                    end_date: '',
                    status: 'planned',
                    contract_value: '',
                    tax_id: null,
                    cost_center_id: null,
                    notes: '',
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddProject}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.code', { defaultValue: 'الكود' })}</label>
                  <input type="text" name="code" value={projectForm.code} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_ar" value={projectForm.name_ar} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}</label>
                  <input type="text" name="name_en" value={projectForm.name_en} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div data-dropdown-toggle onClick={() => { setCustomerDropdownOpen((p) => !p); if (customerDropdownOpen) setCustomerSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
                      <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', color: 'white' }}>{projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItem((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name') : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}</span>
                      <span style={{ fontSize: 12 }}>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddCustomerModal(true)}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '44px',
                        height: '44px'
                      }}
                      title={t('quotations.add.modals.addProject.addCustomer', { defaultValue: 'إضافة عميل جديد' })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <DropdownWithSearch
                    isOpen={customerDropdownOpen}
                    searchTerm={customerSearchTerm}
                    onSearchChange={setCustomerSearchTerm}
                    searchInputRef={customerSearchInputRef}
                    items={customers || []}
                    pagination={customersPagination}
                    onPageChange={setCustomerCurrentPage}
                    onClose={() => setCustomerDropdownOpen(false)}
                    onSelect={(item) => {
                      setProjectForm((prev) => ({ ...prev, customer_id: item.id }));
                      setCustomerDropdownOpen(false);
                      setCustomerSearchTerm('');
                      setCustomerCurrentPage(1);
                    }}
                    label={t('quotations.add.modals.addProject.customer', { defaultValue: 'عميل' })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                  <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                  <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                  <select name="status" value={projectForm.status} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                    <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                    <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                    <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                    <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                  <input type="number"  name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <div data-dropdown-toggle onClick={() => { setTaxDropdownOpen((p) => !p); if (taxDropdownOpen) setTaxSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }}>
                      <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.tax_id ? (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)) ? (isRTL ? ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || '') : ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || '')) : t('quotations.add.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' }) : t('quotations.add.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                      <span style={{ fontSize: 12 }}>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddTaxModal(true)}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '44px',
                        height: '44px'
                      }}
                      title={t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <DropdownWithSearch
                    isOpen={taxDropdownOpen}
                    searchTerm={taxSearchTerm}
                    onSearchChange={setTaxSearchTerm}
                    searchInputRef={taxSearchInputRef}
                    items={taxes || []}
                    pagination={taxesPagination}
                    onPageChange={setTaxCurrentPage}
                    onClose={() => setTaxDropdownOpen(false)}
                    onSelect={(item) => {
                      setProjectForm((prev) => ({ ...prev, tax_id: item.id }));
                      setTaxDropdownOpen(false);
                      setTaxSearchTerm('');
                      setTaxCurrentPage(1);
                    }}
                    label={t('quotations.add.modals.addProject.tax', { defaultValue: 'ضريبة' })}
                  />
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <div data-dropdown-toggle onClick={() => { setCostCenterDropdownOpen((p) => !p); if (costCenterDropdownOpen) setCostCenterSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }}>
                      <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.cost_center_id ? (costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)) ? renderDropdownItem((costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)), 'code', 'name_ar', 'name') : t('quotations.add.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' }) : t('quotations.add.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}</span>
                      <span style={{ fontSize: 12 }}>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddCostCenterModal(true)}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '44px',
                        height: '44px'
                      }}
                      title={t('quotations.add.modals.addProject.addCostCenter', { defaultValue: 'إضافة مركز تكلفة جديد' })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <DropdownWithSearch
                    isOpen={costCenterDropdownOpen}
                    searchTerm={costCenterSearchTerm}
                    onSearchChange={setCostCenterSearchTerm}
                    searchInputRef={costCenterSearchInputRef}
                    items={costCenters || []}
                    pagination={costCentersPagination}
                    onPageChange={setCostCenterCurrentPage}
                    onClose={() => setCostCenterDropdownOpen(false)}
                    onSelect={(item) => {
                      setProjectForm((prev) => ({ ...prev, cost_center_id: item.id }));
                      setCostCenterDropdownOpen(false);
                      setCostCenterSearchTerm('');
                      setCostCenterCurrentPage(1);
                    }}
                    label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                  />
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}</label>
                  <textarea name="notes" value={projectForm.notes} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={isSubmittingProject}
                  style={{
                    backgroundColor: isSubmittingProject ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: isSubmittingProject ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmittingProject ? 0.6 : 1
                  }}
                >
                  {isSubmittingProject ? t('tasks.edit.saving') : t('tasks.edit.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddProjectModal(false);
                    setProjectForm({
                      code: '',
                      name_ar: '',
                      name_en: '',
                      customer_id: null,
                      start_date: '',
                      end_date: '',
                      status: 'planned',
                      contract_value: '',
                      tax_id: null,
                      cost_center_id: null,
                      notes: '',
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('tasks.edit.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal لإضافة عميل جديد */}
      {showAddCustomerModal && createPortal(
        <div
          data-modal-overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddCustomerModal(false)}
        >
          <div
            data-modal-content
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.title', { defaultValue: 'إضافة عميل جديد' })}</h2>
              <button
                onClick={() => {
                  setShowAddCustomerModal(false);
                  setCustomerForm({
                    name_en: '',
                    name_ar: '',
                    code: '',
                    contact_person: '',
                    email: '',
                    phone: '',
                    address: '',
                    tax_number: '',
                    is_active: true,
                    notes: ''
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                {isRTL ? (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                        {t('quotations.add.modals.addCustomer.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_ar"
                        value={customerForm.name_ar}
                        onChange={handleCustomerFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'rtl',
                          textAlign: 'right'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                        {t('quotations.add.modals.addCustomer.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_en"
                        value={customerForm.name_en}
                        onChange={handleCustomerFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'ltr',
                          textAlign: 'left'
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                        {t('quotations.add.modals.addCustomer.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_en"
                        value={customerForm.name_en}
                        onChange={handleCustomerFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'ltr',
                          textAlign: 'left'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                        {t('quotations.add.modals.addCustomer.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_ar"
                        value={customerForm.name_ar}
                        onChange={handleCustomerFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'rtl',
                          textAlign: 'right'
                        }}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('quotations.add.modals.addCustomer.code')}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={customerForm.code}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('quotations.add.modals.addCustomer.contactPerson')}
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={customerForm.contact_person}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('quotations.add.modals.addCustomer.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('quotations.add.modals.addCustomer.phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={customerForm.phone}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('quotations.add.modals.addCustomer.taxNumber')}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={customerForm.tax_number}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('quotations.add.modals.addCustomer.address')}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={customerForm.address}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={customerForm.is_active}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('quotations.add.modals.addCustomer.notes')}
                  </label>
                  <textarea
                    name="notes"
                    value={customerForm.notes}
                    onChange={handleCustomerFormChange}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={isSubmittingCustomer}
                  style={{
                    backgroundColor: isSubmittingCustomer ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmittingCustomer ? 0.6 : 1
                  }}
                >
                  {isSubmittingCustomer ? t('tasks.edit.saving') : t('tasks.edit.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCustomerModal(false);
                    setCustomerForm({
                      name_en: '',
                      name_ar: '',
                      code: '',
                      contact_person: '',
                      email: '',
                      phone: '',
                      address: '',
                      tax_number: '',
                      is_active: true,
                      notes: ''
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('tasks.edit.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal لإضافة ضريبة جديدة */}
      {showAddTaxModal && createPortal(
        <div
          data-modal-overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddTaxModal(false)}
        >
          <div
            data-modal-content
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}</h2>
              <button
                onClick={() => {
                  setShowAddTaxModal(false);
                  setTaxForm({
                    name_ar: '',
                    name_en: '',
                    rate: '',
                    is_active: true,
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_ar" value={taxForm.name_ar} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_en" value={taxForm.name_en} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="number"  name="rate" value={taxForm.rate} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}</label>
                  <select name="is_active" value={taxForm.is_active ? 'true' : 'false'} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="true">{t('quotations.add.modals.addTax.active', { defaultValue: 'نشط' })}</option>
                    <option value="false">{t('quotations.add.modals.addTax.inactive', { defaultValue: 'غير نشط' })}</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={isSubmittingTax}
                  style={{
                    backgroundColor: isSubmittingTax ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: isSubmittingTax ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmittingTax ? 0.6 : 1
                  }}
                >
                  {isSubmittingTax ? t('tasks.edit.saving') : t('tasks.edit.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTaxModal(false);
                    setTaxForm({
                      name_ar: '',
                      name_en: '',
                      rate: '',
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('tasks.edit.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal لإضافة مركز تكلفة جديد */}
      {showAddCostCenterModal && createPortal(
        <div
          data-modal-overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddCostCenterModal(false)}
        >
          <div
            data-modal-content
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCostCenter.title', { defaultValue: 'إضافة مركز تكلفة جديد' })}</h2>
              <button
                onClick={() => {
                  setShowAddCostCenterModal(false);
                  setCostCenterForm({
                    name_ar: '',
                    name_en: '',
                    code: '',
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCostCenter}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCostCenter.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_ar" value={costCenterForm.name_ar} onChange={handleCostCenterFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCostCenter.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_en" value={costCenterForm.name_en} onChange={handleCostCenterFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCostCenter.code', { defaultValue: 'الكود' })}</label>
                  <input type="text" name="code" value={costCenterForm.code} onChange={handleCostCenterFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={isSubmittingCostCenter}
                  style={{
                    backgroundColor: isSubmittingCostCenter ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: isSubmittingCostCenter ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmittingCostCenter ? 0.6 : 1
                  }}
                >
                  {isSubmittingCostCenter ? t('tasks.edit.saving') : t('tasks.edit.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCostCenterModal(false);
                    setCostCenterForm({
                      name_ar: '',
                      name_en: '',
                      code: '',
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('tasks.edit.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const Input = ({ label, isRTL = true, ...rest }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 14 }}>{label}</span>
    <input
      {...rest}
      style={{
        padding: 12,
        borderRadius: 8,
        border: '1px solid #333',
        backgroundColor: '#1a1f2e',
        color: 'white',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}
    />
  </label>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 'var(--form-max-width, 700px)',
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const filterInputStyle = {
  width: '100%',
  padding: 12,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ ...dropdownMenuStyle, direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t('tasks.dropdown.searchPlaceholder')} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.dropdown.page')} {pagination.current_page} {t('tasks.dropdown.of')} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.dropdown.noResults')}</div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
                color: 'white'
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onSelect(item);
                onClose?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              {formatEntity(item, i18n.language)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatEntity = (entity, language = 'ar') => {
  if (!entity) return '—';
  // Handle employees (have 'name' and 'employee_code')
  if (entity.employee_code !== undefined || entity.name) {
    const code = entity.employee_code ? `${entity.employee_code} - ` : '';
    return `${code}${entity.name || ''}` || `#${entity.id}`;
  }
  // Handle other entities (have 'name_ar'/'name_en' and 'code')
  const code = entity.code ? `${entity.code} - ` : '';
  const isRTL = language === 'ar';
  const name = isRTL
    ? (entity.name_ar || entity.name_en || '')
    : (entity.name_en || entity.name_ar || '');
  return `${code}${name}` || `#${entity.id}`;
};

const DropdownWithSearchMultiple = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onToggleSelect,
  onClose,
  label,
  selectedIds = [],
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  const isSelected = (itemId) => {
    return selectedIds.includes(String(itemId));
  };

  return (
    <div style={{ ...dropdownMenuStyle, direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t('tasks.dropdown.searchPlaceholder')} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.dropdown.page')} {pagination.current_page} {t('tasks.dropdown.of')} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('tasks.dropdown.noResults')}</div>
        ) : (
          (Array.isArray(items) ? items : []).map((item, index) => {
            const selected = isSelected(item.id);
            return (
              <div
                key={item.id}
                style={{
                  padding: '10px 12px',
                  borderTop: index === 0 ? 'none' : '1px solid #333',
                  cursor: 'pointer',
                  backgroundColor: selected ? '#2a2a2a' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Only toggle if click is not on checkbox
                  if (e.target.type !== 'checkbox') {
                    onToggleSelect(item);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSelect(item);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSelect(item);
                  }}
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                />
                <span
                  style={{
                    flex: 1,
                    cursor: 'pointer',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    userSelect: 'none',
                    color: 'white'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSelect(item);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {formatEntity(item, i18n.language)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EditTasks;

